/**
 * DashboardWidget Component
 * Renders a single dashboard widget with its data
 */

import React, { useState } from 'react';
import { Card, Spin, Empty, Tag, Button, Dropdown, Badge, List, Calendar, Typography, Space } from 'antd';
import {
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import type { DashboardWidget as Widget, WidgetData, WidgetDataItem } from '@/api/dashboard-widgets';
import { dashboardWidgetsApi } from '@/api/dashboard-widgets';

const { Text } = Typography;

interface DashboardWidgetProps {
  widget: Widget;
  onEdit?: (widget: Widget) => void;
  onDelete?: (widget: Widget) => void;
}

const DashboardWidget: React.FC<DashboardWidgetProps> = ({
  widget,
  onEdit,
  onDelete,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Fetch widget data
  const { data, isLoading, refetch, error } = useQuery({
    queryKey: ['widget-data', widget.id],
    queryFn: () => dashboardWidgetsApi.getData(widget.id),
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });

  const menuItems = [
    {
      key: 'edit',
      icon: <EditOutlined />,
      label: 'Edit Widget',
      onClick: () => onEdit?.(widget),
    },
    {
      key: 'refresh',
      icon: <ReloadOutlined />,
      label: 'Refresh',
      onClick: () => refetch(),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Delete',
      danger: true,
      onClick: () => onDelete?.(widget),
    },
  ];

  const renderContent = () => {
    if (isLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 100 }}>
          <Spin />
        </div>
      );
    }

    if (error) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Failed to load data"
        >
          <Button size="small" onClick={() => refetch()}>
            Retry
          </Button>
        </Empty>
      );
    }

    if (!data) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />;
    }

    switch (widget.widget_type) {
      case 'count':
        return renderCount(data);
      case 'list':
        return renderList(data);
      case 'calendar':
        return renderCalendar(data);
      default:
        return <Empty description="Unknown widget type" />;
    }
  };

  const renderCount = (data: WidgetData) => {
    const count = data.count ?? 0;
    const isOverdue = widget.filter_config.status?.includes('overdue');
    const accentColor = isOverdue && count > 0 ? 'var(--status-error)' : 'var(--accent-ink)';

    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: 16 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
            {isOverdue
              ? <ExclamationCircleOutlined style={{ color: accentColor, fontSize: 18 }} />
              : <ClockCircleOutlined style={{ color: accentColor, fontSize: 18 }} />
            }
            <span style={{
              fontFamily: 'var(--font-mono)',
              fontVariantNumeric: 'tabular-nums',
              fontSize: widget.grid_width > 1 ? 56 : 42,
              fontWeight: 600,
              lineHeight: 1,
              color: accentColor,
            }}>
              {count}
            </span>
          </div>
          <div style={{
            marginTop: 8,
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-tertiary)',
          }}>
            {widget.title}
          </div>
        </div>
      </div>
    );
  };

  const renderList = (data: WidgetData) => {
    const items = data.items ?? [];

    if (items.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No work packages found"
          style={{ padding: '20px 0' }}
        />
      );
    }

    return (
      <List
        size="small"
        dataSource={items}
        style={{ maxHeight: (widget.grid_height * 150) - 60, overflow: 'auto' }}
        renderItem={(item: WidgetDataItem) => (
          <List.Item
            style={{
              padding: '6px 0',
              borderBottom: '1px solid var(--border-subtle)',
              borderLeft: item.is_overdue ? '2px solid var(--status-error)' : '2px solid transparent',
              paddingLeft: 8,
              backgroundColor: item.is_overdue ? 'var(--status-error-subtle)' : undefined,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text
                  ellipsis
                  style={{ flex: 1, fontSize: 13, color: 'var(--text-primary)' }}
                  title={item.template_title}
                >
                  {item.template_title}
                </Text>
                {item.status === 'completed' ? (
                  <CheckCircleOutlined style={{ color: 'var(--status-success)', flexShrink: 0 }} />
                ) : item.is_overdue ? (
                  <ExclamationCircleOutlined style={{ color: 'var(--status-error)', flexShrink: 0 }} />
                ) : null}
              </div>
              <Space size={8} style={{ marginTop: 3 }}>
                {item.category_name && (
                  <Tag
                    style={{
                      marginRight: 0,
                      borderRadius: 2,
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderColor: item.category_color || 'var(--border-default)',
                    }}
                  >
                    {item.category_name}
                  </Tag>
                )}
                {widget.display_config.showDueDate !== false && item.due_date && (
                  <span className="bp-mono" style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                    <ClockCircleOutlined style={{ marginRight: 3 }} />
                    {dayjs(item.due_date).format('MMM D')}
                  </span>
                )}
                {widget.display_config.showAssignee !== false && item.assignee_name && (
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-tertiary)' }}>
                    <UserOutlined style={{ marginRight: 3 }} />
                    {item.assignee_name}
                  </span>
                )}
              </Space>
            </div>
          </List.Item>
        )}
      />
    );
  };

  const renderCalendar = (data: WidgetData) => {
    const dates = data.dates ?? {};

    const dateCellRender = (date: dayjs.Dayjs) => {
      const dateStr = date.format('YYYY-MM-DD');
      const items = dates[dateStr] || [];

      if (items.length === 0) return null;

      const hasOverdue = items.some((item: WidgetDataItem) => item.is_overdue);

      return (
        <Badge
          count={items.length}
          size="small"
          style={{ backgroundColor: hasOverdue ? 'var(--status-error)' : 'var(--status-info)' }}
        />
      );
    };

    return (
      <div style={{ padding: 8 }}>
        <Calendar
          fullscreen={false}
          cellRender={(date) => dateCellRender(dayjs(date))}
        />
      </div>
    );
  };

  // Calculate card height based on grid height
  const cardHeight = widget.grid_height * 150;

  return (
    <Card
      size="small"
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text
            ellipsis
            style={{
              flex: 1,
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              color: 'var(--text-secondary)',
            }}
          >
            {widget.title}
          </Text>
        </div>
      }
      extra={
        <Dropdown menu={{ items: menuItems }} trigger={['click']}>
          <Button
            type="text"
            size="small"
            icon={<MoreOutlined />}
            style={{ opacity: isHovered ? 1 : 0.3, transition: 'opacity 0.15s' }}
          />
        </Dropdown>
      }
      style={{
        height: cardHeight,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface-1)',
        border: '1px solid var(--border-default)',
        borderRadius: 2,
        boxShadow: 'none',
      }}
      styles={{
        header: {
          borderBottom: '1px solid var(--border-subtle)',
          minHeight: 36,
          padding: '0 12px',
        },
        body: {
          flex: 1,
          overflow: 'hidden',
          padding: widget.widget_type === 'count' ? 0 : '8px 12px',
        },
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {renderContent()}
    </Card>
  );
};

export default DashboardWidget;
